import React, { useState } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Separator } from 'src/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';

import useOrganizationConfig from 'src/api/core/organization-config/useOrganizationConfig';
import useUpdateOrganizationConfig from 'src/api/core/organization-config/useUpdateOrganizationConfig';
import { useMutation } from '@tanstack/react-query';
import { rpcClient } from '@/api/rpc/client';

const commonTimezones = [
  { label: 'UTC', value: 'UTC' },
  { label: 'US Eastern (America/New_York)', value: 'America/New_York' },
  { label: 'US Central (America/Chicago)', value: 'America/Chicago' },
  { label: 'US Mountain (America/Denver)', value: 'America/Denver' },
  { label: 'US Pacific (America/Los_Angeles)', value: 'America/Los_Angeles' },
];

const OrganizationSettingsCard: React.FC = () => {
  const { data: orgConfigData, isLoading, error } = useOrganizationConfig();
  const updateOrgConfig = useUpdateOrganizationConfig();

  const [isEditing, setIsEditing] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [timezone, setTimezone] = useState('UTC');

  const seedDatabase = useMutation({
    mutationFn: async (input?: { seedId?: string }) => {
      return await rpcClient.demo.seeding.seed(input || {});
    },
  });

  const resetDatabase = useMutation({
    mutationFn: async () => {
      return await rpcClient.demo.seeding.reset();
    },
  });

  const orgConfig = orgConfigData?.organizationConfig;

  // Initialize form state when data loads or editing starts
  React.useEffect(() => {
    if (orgConfig && isEditing) {
      setLogoUrl(orgConfig.logoUrl || '');
      setTimezone(orgConfig.timezone || 'UTC');
    }
  }, [orgConfig, isEditing]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      // Save organization config (logo, etc.)
      await updateOrgConfig.mutateAsync({
        logoUrl: logoUrl.trim() || null,
        timezone,
      });

      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update configuration:', error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-red-800 mb-2">
            Error Loading Configuration
          </h3>
          <p className="text-red-600">
            Failed to load organization configuration. Please try again.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      <Card className="shadow-lg border border-gray-200">
        <CardHeader className="pb-4">
          <div className="flex justify-between items-baseline">
            <div className="mb-1">
              <CardTitle
                className="text-xl font-semibold mb-2"
                style={{ color: '#344767', padding: '12px' }}
              >
                Organization Configuration
              </CardTitle>
              <CardDescription
                className="text-sm text-gray-600 leading-relaxed"
                style={{ padding: '12px' }}
              >
                {`Manage this organization's modules and branding settings`}
              </CardDescription>
            </div>

            {!isEditing ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleEdit}
                disabled={updateOrgConfig.isLoading}
                className="text-gray-700 border-gray-300 hover:bg-gray-50"
              >
                Edit
              </Button>
            ) : (
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  className="text-gray-700 border-gray-300 hover:bg-gray-50"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={updateOrgConfig.isLoading}
                  className="bg-gray-800 hover:bg-gray-900 text-white"
                >
                  {updateOrgConfig.isLoading ? 'Saving...' : 'Save'}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-6 pt-0" style={{ padding: '12px' }}>
          <div>
            <Label className="text-sm font-medium" style={{ color: '#344767' }}>
              Organization
            </Label>
            <p className="text-sm text-gray-600 mt-1">
              {orgConfig?.organization?.name || 'Not available'}
            </p>
          </div>

          <Separator className="border-gray-200" />

          <div className="space-y-2">
            <Label className="text-sm font-medium" style={{ color: '#344767' }}>
              Timezone
            </Label>
            {isEditing ? (
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger id="timezone" className="border-gray-300">
                  <SelectValue placeholder="Select a timezone" />
                </SelectTrigger>
                <SelectContent>
                  {commonTimezones.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm text-gray-600 mt-2">
                {orgConfig?.timezone || 'UTC'}
              </p>
            )}
            <p className="text-xs text-gray-500">
              Used for workflow scheduling, cron evaluation, and payment date
              calculations.
            </p>
          </div>

          <Separator className="border-gray-200" />

          <div>
            <Label className="text-sm font-medium" style={{ color: '#344767' }}>
              Logo URL
            </Label>
            {isEditing ? (
              <div className="mt-2">
                <Input
                  type="url"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://example.com/logo.png"
                  className="border-gray-300 focus:border-gray-500"
                />
              </div>
            ) : (
              <div className="flex items-center space-x-3 mt-2">
                <p className="text-sm text-gray-600">
                  {orgConfig?.logoUrl || 'No logo configured'}
                </p>
                {orgConfig?.logoUrl && (
                  <img
                    src={orgConfig.logoUrl}
                    alt="Organization logo"
                    className="h-8 w-8 object-contain rounded border"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
              </div>
            )}
          </div>

          <Separator className="border-gray-200" />

          <div className="space-y-4">
            <Label className="text-sm font-medium" style={{ color: '#344767' }}>
              Demo Database
            </Label>
            <p className="text-xs text-gray-500">
              Seed or reset demo data for testing purposes
            </p>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => seedDatabase.mutate({})}
                disabled={seedDatabase.isPending}
                className="text-green-700 border-green-300 hover:bg-green-50"
              >
                {seedDatabase.isPending ? 'Seeding...' : 'Seed Database'}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (
                    window.confirm(
                      'Are you sure you want to delete all demo data? This cannot be undone.',
                    )
                  ) {
                    resetDatabase.mutate();
                  }
                }}
                disabled={resetDatabase.isPending}
                className="text-red-700 border-red-300 hover:bg-red-50"
              >
                {resetDatabase.isPending ? 'Resetting...' : 'Reset Database'}
              </Button>
            </div>

            {seedDatabase.isSuccess && (
              <div className="text-sm text-green-700 bg-green-50 p-2 rounded">
                {seedDatabase.data.message}
                <ul className="mt-1 ml-4 list-disc">
                  {seedDatabase.data.results.map((result) => (
                    <li key={result.name}>
                      {result.name}: {result.count} records
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {seedDatabase.isError && (
              <div className="text-sm text-red-700 bg-red-50 p-2 rounded">
                Error seeding database. Please try again.
              </div>
            )}

            {resetDatabase.isSuccess && (
              <div className="text-sm text-green-700 bg-green-50 p-2 rounded">
                {resetDatabase.data.message}
              </div>
            )}

            {resetDatabase.isError && (
              <div className="text-sm text-red-700 bg-red-50 p-2 rounded">
                Error resetting database. Please try again.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrganizationSettingsCard;
